import LimitedTimeOfferLifecycle from './components/ShowModal/LimitedTimeOfferLifecycle'
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom'
import { Suspense, lazy, useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import Navbar from './scenes/Editor/components/Navbar'
import ShowModal from './components/ShowModal'
import Loading from './components/Loading'
import { URLLockManager } from './scenes/Editor/components/Navbar/URLLockManager'
import useAppContext from './hooks/useAppContext'
import useModal from './hooks/useModal'
import useNetworkStatus from './hooks/useNetworkStatus'
import MessageToast from './scenes/Editor/components/MessageToast'
import { NON_SIGNUP_USER_MESSAGE, ShowModalType } from './scenes/engine/common/constants'
import { useEditorContext } from './scenes/engine'
import { selectUser } from './store/slices/user/selectors'
import DeeplinkParser from './deeplink/DeeplinkParser'
import NetworkStatusService from './shared/services/NetworkStatusService'

// Build-time guards: production does not include the simulation panel chunk.
const LimitedTimeOfferQaPanel = process.env.REACT_APP_OFFER_QA_ENABLED === 'true' && process.env.REACT_APP_IS_SANDBOX === 'true'
  && new URLSearchParams(window.location.search).get('offerQa') === '1'
  ? lazy(() => import('./components/ShowModal/qa/LimitedTimeOfferQaPanel')) : null

// Lazy load main routes
const Editor = lazy(() => import('./scenes/Editor'))
const Dashboard = lazy(() => import('./scenes/Dashboard'))
const Projects = lazy(() => import('./scenes/Dashboard/Projects'))
const Feed = lazy(() => import('./scenes/Dashboard/Feed'))
const AIModels = lazy(() => import('./scenes/Dashboard/AIFashionModels'))
const Photoshoots = lazy(() => import('./scenes/Dashboard/Photoshoots'))
const TemplateManagerView = lazy(() => import('./views/TemplateManagerView'))
const TemplateGeneratorView = lazy(() => import('./views/TemplateGeneratorView'))
const CategoryManagerView = lazy(() => import('./views/CategoryManagerView'))
const Sso = lazy(() => import('./scenes/Sso'))
const AIStudio = lazy(() => import('./scenes/AIStudioLite/AIStudio'))
const BatchStudio = lazy(() => import('./scenes/BatchStudio/BatchStudio'))
const PhotoshootHandoff = lazy(() => import('./scenes/PhotoshootHandoff'))
const WhatshotHandoff = lazy(() => import('./scenes/WhatshotHandoff'))

function useURLLockManager() {
  const location = useLocation()
  const lockManager = URLLockManager.getInstance()

  useEffect(() => {
    const currentURL = location.pathname + location.search
    lockManager.handleURLChange(currentURL)

    // Cleanup function on unmount
    return () => {
      lockManager.clear()
    }
  }, [location, lockManager])

  return null
}

// Separate component for URL lock management inside Router context
const URLLockManagerWrapper = () => {
  useURLLockManager() // Calling the hook within the Router context
  return null
}

const Routes = () => {
  const { setOpenToast, openToast, isLoadedJsonSuccess, setIsLoadedJsonSuccess } =
    useAppContext()
  const { setShowModalType } = useModal()
  const { editor } = useEditorContext()
  const user = useSelector(selectUser)

  const handleNetworkChange = useCallback(
    (online: boolean) => {
      setOpenToast(prev => {
        if (!online) {
          return 'noInternet';
        }

        if (prev === 'noInternet' || prev === 'reconnecting') {
          if (!user && editor?.handlers?.autoSaveHandler?.saveState === 1) {
            setTimeout(() => setOpenToast('nonSignUpUser'), NON_SIGNUP_USER_MESSAGE);
            return '';
          } else {
            setTimeout(() => setOpenToast(''), 3000);
            return 'connectionRestored';
          }
        }

        return prev;
      })
 
    },
    [user, editor],
  )

  useNetworkStatus(handleNetworkChange)

  useEffect(() => {
    if (isLoadedJsonSuccess) return

    let cancelled = false
    NetworkStatusService.getInstance().getLatestStatusConnection().then((online) => {
      if (cancelled) return
      if (!online) {
        setShowModalType(ShowModalType.NO_INTERNET)
        editor?.handlers?.objectsHandler?.clear()
      }
    })

    return () => { cancelled = true }
  }, [isLoadedJsonSuccess, editor, setShowModalType])

  useEffect(() => {
    const handleLeaveProject = function (e) {
      if (editor?.handlers?.transactionHandler && !editor.handlers.transactionHandler.isFlushed) {
        e.preventDefault()
        e.stopPropagation()
        // e.stopImmediatePropagation()
        e.returnValue = '' // Required for Chrome
      }
    }
    window.addEventListener('beforeunload', handleLeaveProject)
    return () => {
      window.removeEventListener('beforeunload', handleLeaveProject)
    }
  }, [editor])

  return (
    <Router>
      <URLLockManagerWrapper />
      <DeeplinkParser />
      {LimitedTimeOfferQaPanel && <Suspense fallback={null}><LimitedTimeOfferQaPanel /></Suspense>}
      <Suspense fallback={<Loading />}>
        <Navbar />
        <MessageToast />
        <Switch>
          <Route path="/photoshoot-handoff" component={PhotoshootHandoff} />
          <Route path="/whatshot-handoff" component={WhatshotHandoff} />
          <Route path="/sso" component={Sso} />
          <Route path="/generate-templates" component={TemplateGeneratorView} />
          <Route path="/manage-templates" component={TemplateManagerView} />
          <Route path="/manage-categories" component={CategoryManagerView} />
          <Route path="/design/:id/edit" component={Editor} />
          <Route path="/ai-studio" component={AIStudio} />
          <Route path="/batch-studio" component={BatchStudio} />
          <Route exact path="/create/batch" render={() => <Redirect to="/batch-studio" />} />
          <Route path="/create/projects" component={Projects} />
          <Route path="/create/feed" component={Feed} />
          <Route path="/create/ai-models" component={AIModels} />
          <Route path="/create/photoshoots" component={Photoshoots} />
          <Route path="/create" component={Dashboard} />
          <Route path="/" component={Editor} />
        </Switch>
        <LimitedTimeOfferLifecycle />
        <ShowModal />
      </Suspense>
    </Router>
  )
}

export default Routes
